/**
 * Warnings for a set of posture requirements (VSNP-1957): the platforms that can
 * never meet them (a fact they always report as unknown, or as false), keys not
 * offered in this release, and requirements that also admit unknown. Shown in
 * the Posture Profile modal and under the profile picked in a policy.
 *
 * Renders nothing when there is nothing to say.
 */

import { AlertTriangle } from 'lucide-react';
import type { PosturePredicate } from '../../services/access.service';
import { platformList, profileWarnings } from '../../data/postureCatalog';

interface PostureWarningsProps {
  requirements: Record<string, PosturePredicate> | null | undefined;
  /** What the requirements belong to, for the heading ("this profile"). */
  subject?: string;
  testId?: string;
  /** False when each requirement already shows its own warnings (the profile editor): only the summary line. */
  listItems?: boolean;
}

const PostureWarnings = ({
  requirements,
  subject = 'this profile',
  testId = 'posture-warnings',
  listItems = true,
}: PostureWarningsProps) => {
  const { shortfalls, notes, neverPass } = profileWarnings(requirements);
  if (listItems ? shortfalls.length === 0 && notes.length === 0 : neverPass.length === 0) return null;

  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={testId} role="note">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
        <div className="space-y-1">
          {neverPass.length > 0 && (
            <p className="font-medium">
              {platformList(neverPass)} devices can never meet {subject}.
            </p>
          )}
          {listItems && (shortfalls.length > 0 || notes.length > 0) && (
            <ul className="list-disc pl-4 space-y-0.5">
              {shortfalls.map((s) => (
                <li key={`${s.key}-${s.platform}`}>{s.message}</li>
              ))}
              {notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}
          {shortfalls.some((s) => s.reports === 'unknown') && (
            <p className="text-amber-700">
              Unknown counts as not met: a device that cannot measure a fact reports unknown, and unknown never equals
              pass.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostureWarnings;
